import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Alert.css';

const Alert = ({ 
  show, 
  message, 
  onHide, 
  duration = 1200, 
  type = 'warning',
  autoHide = true 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationState, setAnimationState] = useState(''); // 'enter', 'show', 'exit'
  const timersRef = useRef({}); // 用于存储所有定时器ID
  const prevShowRef = useRef(show); // 用于跟踪show的前一个值

  // 清理所有定时器的函数
  const clearAllTimers = useCallback(() => {
    Object.values(timersRef.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });
    timersRef.current = {};
  }, []);

  useEffect(() => {
    if (show) {
      // 清理之前的所有定时器，避免冲突
      clearAllTimers();
      
      setIsVisible(true);
      setAnimationState('enter');
      
      // 进入动画完成后切换到显示状态
      timersRef.current.enterTimer = setTimeout(() => {
        setAnimationState('show');
        
        if (autoHide) {
          // 显示0.5秒后开始退出动画
          timersRef.current.showTimer = setTimeout(() => {
            setAnimationState('exit');
            
            // 退出动画完成后隐藏组件
            timersRef.current.exitTimer = setTimeout(() => {
              setIsVisible(false);
              setAnimationState('');
            }, 800); // 退出动画时长
          }, 500); // 显示时长0.5秒
        }
      }, 300); // 进入动画时长

      return clearAllTimers;
    }
  }, [show, autoHide]);

  // 处理手动隐藏 - 只在show从true变为false时触发
  useEffect(() => {
    const prevShow = prevShowRef.current;
    prevShowRef.current = show;
    
    // 只有当show从true变为false，且当前组件可见时，才触发手动隐藏
    if (prevShow && !show && isVisible) {
      // 手动触发隐藏
      clearAllTimers();
      setAnimationState('exit');
      
      timersRef.current.exitTimer = setTimeout(() => {
        setIsVisible(false);
        setAnimationState('');
      }, 800);
      
      return clearAllTimers;
    }
  }, [show, isVisible, clearAllTimers]);

  // 当组件完全隐藏后，调用onHide回调
  // 使用ref跟踪是否曾经显示过，避免初始状态就调用onHide
  const hasBeenVisibleRef = useRef(false);
  
  useEffect(() => {
    if (isVisible) {
      hasBeenVisibleRef.current = true;
    }
  }, [isVisible]);
  
  useEffect(() => {
    // 只有曾经显示过，现在才隐藏的情况下才调用onHide
    if (!isVisible && hasBeenVisibleRef.current && onHide) {
      onHide();
      hasBeenVisibleRef.current = false; // 重置标记
    }
  }, [isVisible, onHide]);

  // 组件卸载时清理所有定时器
  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  if (!isVisible) return null;

  return (
    <div className="alert-overlay">
      <div className={`alert-box alert-${type} alert-${animationState}`}>
        <div className="alert-content">
          <div className="alert-message">
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alert;
